import React from 'react';

import { FormattedMessage, useIntl } from '../../../util/reactIntl';

import css from './SectionJobOptionalDetails.module.css';

const DetailRow = ({ icon, labelId, value }) => {
  if (!value) return null;
  return (
    <div className={css.detailRow}>
      <dt className={css.detailLabel}>
        {icon ? <span className={css.detailIcon}>{icon}</span> : null}
        <FormattedMessage id={labelId} />
      </dt>
      <dd className={css.detailValue}>{value}</dd>
    </div>
  );
};

/**
 * Optional job fields shown in the main column above the description.
 */
const SectionJobOptionalDetails = props => {
  const { publicData } = props;
  const intl = useIntl();

  const {
    arrival_time,
    end_time,
    venue_name,
    amenities_provided,
    dress_code,
    parking_instructions,
    loading_dock_instructions,
    floral_tools_provided,
    compensation_notes,
    additional_notes,
  } = publicData || {};

  const amenityLabels = {
    hotel: intl.formatMessage({ id: 'SectionJobDetails.amenityHotel' }),
    meals: intl.formatMessage({ id: 'SectionJobDetails.amenityMeals' }),
    mileage: intl.formatMessage({ id: 'SectionJobDetails.amenityMileage' }),
    flight: intl.formatMessage({ id: 'SectionJobDetails.amenityFlight' }),
    rental_car: intl.formatMessage({ id: 'SectionJobDetails.amenityRentalCar' }),
    per_diem: intl.formatMessage({ id: 'SectionJobDetails.amenityPerDiem' }),
  };

  const amenitiesText = Array.isArray(amenities_provided) && amenities_provided.length
    ? amenities_provided.map(k => amenityLabels[k] || k).join(', ')
    : null;

  const floralToolsText =
    floral_tools_provided === 'yes'
      ? intl.formatMessage({ id: 'SectionJobDetails.yes' })
      : floral_tools_provided === 'no'
        ? intl.formatMessage({ id: 'SectionJobDetails.no' })
        : null;

  const hasDetailRows = !!(
    arrival_time ||
    end_time ||
    venue_name ||
    amenitiesText ||
    dress_code ||
    parking_instructions ||
    loading_dock_instructions ||
    floralToolsText
  );
  const hasNotes = !!(compensation_notes || additional_notes);

  if (!hasDetailRows && !hasNotes) {
    return null;
  }

  return (
    <section className={css.root}>
      <h3 className={css.heading}>
        <FormattedMessage
          id="SectionJobOptionalDetails.heading"
          defaultMessage="Job details"
        />
      </h3>

      {hasDetailRows ? (
        <dl className={css.detailsList}>
          <DetailRow icon="🕐" labelId="SectionJobDetails.arrivalTime" value={arrival_time} />
          <DetailRow icon="🕕" labelId="SectionJobDetails.endTime" value={end_time} />
          <DetailRow icon="🏛️" labelId="SectionJobDetails.venueName" value={venue_name} />
          <DetailRow
            icon="🎁"
            labelId="SectionJobDetails.amenitiesProvided"
            value={amenitiesText}
          />
          <DetailRow icon="👔" labelId="SectionJobDetails.dressCode" value={dress_code} />
          <DetailRow
            icon="🅿️"
            labelId="SectionJobDetails.parkingInstructions"
            value={parking_instructions}
          />
          <DetailRow
            icon="🚚"
            labelId="SectionJobDetails.loadingDockInstructions"
            value={loading_dock_instructions}
          />
          <DetailRow
            icon="🔧"
            labelId="SectionJobDetails.floralToolsProvided"
            value={floralToolsText}
          />
        </dl>
      ) : null}

      {compensation_notes ? (
        <div className={css.notesBlock}>
          <p className={css.notesLabel}>
            <FormattedMessage
              id="SectionJobDetails.compensationNotes"
              defaultMessage="Compensation details"
            />
          </p>
          <p className={css.notesText}>{compensation_notes}</p>
        </div>
      ) : null}

      {additional_notes ? (
        <div className={css.notesBlock}>
          <p className={css.notesLabel}>
            <FormattedMessage
              id="SectionJobDetails.additionalNotes"
              defaultMessage="Additional notes"
            />
          </p>
          <p className={css.notesText}>{additional_notes}</p>
        </div>
      ) : null}
    </section>
  );
};

export default SectionJobOptionalDetails;
